(ns minorodata.pipeline-dag.layout-cache
  "ELK replies cached in memory and in localStorage per user, keyed on a fingerprint of
   the input; only the position tree is kept, since routed edges are discarded."
  (:require [applied-science.js-interop :as j]))

(def ^:private mem-size 8)
(def ^:private ls-max-entries 3)
(def ^:private ls-max-bytes 1500000)

(defonce ^:private mem (atom {:order [] :entries {}}))

;; Parsed localStorage store per user, so the JSON is parsed once per session.
(defonce ^:private ls-mirror (atom {}))

(defn- mem-put! [fp v]
  (swap! mem
         (fn [{:keys [order entries]}]
           (let [order' (conj (filterv #(not= fp %) order) fp)
                 drop-n (- (count order') mem-size)]
             {:order   (subvec order' (max 0 drop-n))
              :entries (apply dissoc (assoc entries fp v) (take drop-n order'))}))))

(defn- prune-reply
  "ELK's reply reduced to the id/position/size tree `flatten-layout` walks."
  [^js g]
  (let [node (fn node [^js n]
               (let [kids (j/get n :children)]
                 (cond-> #js {:id     (j/get n :id)
                              :x      (j/get n :x)
                              :y      (j/get n :y)
                              :width  (j/get n :width)
                              :height (j/get n :height)}
                   (and kids (pos? (alength kids)))
                   (j/assoc! :children (.map kids node)))))]
    #js {:children (.map (or (j/get g :children) #js []) node)}))

(defn- fingerprint
  "Hash of the ELK input, with node and edge counts as a collision guard."
  [input]
  (str (hash input) "-"
       (count (:children input)) "-"
       (count (:edges input))))

(defn- ls-key [user-id]
  (str "dag-layout-cache-v1-" user-id))

(defn- ls-read [user-id]
  (or (get @ls-mirror user-id)
      (try
        (when-let [store (some-> (js/localStorage.getItem (ls-key user-id)) js/JSON.parse)]
          (swap! ls-mirror assoc user-id store)
          store)
        (catch :default _ nil))))

(defn- ls-get [user-id fp]
  (try
    (some-> (ls-read user-id) (j/get fp) (j/get :layout))
    (catch :default _ nil)))

(defn- ls-put! [user-id fp ^js pruned]
  (try
    (let [store   (or (ls-read user-id) #js {})
          entries (->> (js/Object.entries store)
                       (sort-by (fn [^js e] (j/get (aget e 1) :at)))
                       (take-last (dec ls-max-entries))
                       (reduce (fn [^js acc ^js e] (j/assoc! acc (aget e 0) (aget e 1)))
                               #js {}))
          json    (js/JSON.stringify
                   (j/assoc! entries fp #js {:at (js/Date.now) :layout pruned}))]
      (when (< (.-length json) ls-max-bytes)
        (js/localStorage.setItem (ls-key user-id) json)
        (swap! ls-mirror assoc user-id entries)))
    (catch :default _
      ;; storage full or unavailable — caching must never fail a layout
      (swap! ls-mirror dissoc user-id)
      (try (js/localStorage.removeItem (ls-key user-id)) (catch :default _ nil)))))

(defn through
  "Promise of ELK's reply for `input`: `user-id`'s cached one, or `(compute)`'s, which is then cached."
  [user-id input compute]
  (let [fp  (fingerprint input)
        mem-key [user-id fp]]
    (if-let [hit (or (get-in @mem [:entries mem-key])
                     (ls-get user-id fp))]
      (do (mem-put! mem-key hit)
          (js/Promise.resolve hit))
      (-> (compute)
          (.then (fn [g]
                   (let [pruned (prune-reply g)]
                     (mem-put! mem-key pruned)
                     (ls-put! user-id fp pruned))
                   g))))))
